import { useRef } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { DayPicker, useDayRender } from 'react-day-picker';
import SelectControl from '../SelectControl';
import { cn, formatDate } from '../../lib/utils';
import { buttonVariants } from './button';

const CalendarDropdown = ({ name, value, onChange, children, 'aria-label': label }) => (
  <SelectControl
    name={name}
    value={value}
    onChange={onChange}
    aria-label={label}
    className="h-8 w-auto min-w-[5rem] px-2 py-1 shadow-none"
    searchable={name === 'years'}
    searchPlaceholder="Search years…"
  >
    {children}
  </SelectControl>
);
const CalendarDay = ({ date, displayMonth }) => {
  const ref = useRef(null);
  const day = useDayRender(date, displayMonth, ref);
  if (day.isHidden) return <div aria-hidden="true" />;
  if (!day.isButton) return <div {...day.divProps} />;
  return <button {...day.buttonProps} ref={ref} type="button" aria-label={`Choose ${formatDate(date)}`} />;
};

// shadcn Calendar pattern, using DayPicker v8 for this application's React 17 runtime.
export const Calendar = ({ className, classNames, showOutsideDays = true, ...props }) => (
  <DayPicker
    showOutsideDays={showOutsideDays}
    className={cn('p-3', className)}
    classNames={{
      months: 'flex flex-col space-y-4 sm:flex-row sm:space-x-4 sm:space-y-0',
      month: 'space-y-4',
      caption: 'relative flex items-center justify-center pt-1',
      caption_label: 'text-sm font-semibold',
      caption_dropdowns: 'flex items-center justify-center gap-1 px-8',
      dropdown: 'rounded-md border border-slate-300 bg-white px-1 py-1 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500',
      dropdown_month: 'relative',
      dropdown_year: 'relative',
      vhidden: 'sr-only',
      nav: 'flex items-center gap-1',
      nav_button: cn(buttonVariants({ variant: 'outline' }), 'h-7 w-7 bg-transparent p-0'),
      nav_button_previous: 'absolute left-0',
      nav_button_next: 'absolute right-0',
      table: 'w-full border-collapse',
      head_row: 'flex',
      head_cell: 'w-9 rounded-md text-xs font-normal text-slate-500',
      row: 'mt-2 flex w-full',
      cell: 'relative h-9 w-9 p-0 text-center text-sm focus-within:relative focus-within:z-20',
      day: cn(buttonVariants({ variant: 'ghost' }), 'h-9 w-9 p-0 font-normal aria-selected:opacity-100'),
      day_selected: '!bg-blue-700 !text-white hover:!bg-blue-800 focus:!bg-blue-700',
      day_today: 'bg-slate-100 font-bold text-slate-950',
      day_outside: 'text-slate-400 opacity-50',
      day_disabled: 'text-slate-400 opacity-40',
      day_hidden: 'invisible',
      ...classNames,
    }}
    components={{ Dropdown: CalendarDropdown, Day: CalendarDay, IconLeft: () => <ChevronLeft className="h-4 w-4" />, IconRight: () => <ChevronRight className="h-4 w-4" /> }}
    {...props}
  />
);
